// ============================================================
// CategorySelector.jsx
// Step 2: Select which audit categories/programs apply
// ============================================================

import React, { useState } from "react";

// Preset bundles for quick selection
const PRESETS = [
  {
    label: "Top 10 Core Programs",
    ids: [
      "walking_working", "emergency_action", "exit_routes", "machine_guarding",
      "lockout_tagout", "electrical", "ppe", "hazcom", "fire_prevention", "recordkeeping",
    ],
  },
  {
    label: "Manufacturing Facility",
    ids: [
      "walking_working", "emergency_action", "fire_prevention", "exit_routes",
      "first_aid", "powered_industrial_trucks", "machine_guarding", "lockout_tagout",
      "electrical", "ppe", "hazcom", "hearing_conservation", "respiratory_protection",
      "compressed_gases", "flammable_liquids", "materials_handling", "welding_cutting",
      "recordkeeping",
    ],
  },
  {
    label: "Warehouse / Distribution",
    ids: [
      "walking_working", "emergency_action", "exit_routes", "first_aid",
      "powered_industrial_trucks", "electrical", "ppe", "hazcom",
      "materials_handling", "fire_prevention", "flammable_liquids", "recordkeeping",
    ],
  },
  {
    label: "All 23 Categories",
    ids: null, // means all
  },
];

export default function CategorySelector({ categories, selected, onComplete, onBack }) {
  const [checked, setChecked] = useState(new Set(selected));

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const applyPreset = (preset) => {
    const ids = preset.ids ? preset.ids : categories.map((c) => c.id);
    setChecked(new Set(ids));
  };

  const handleAll = () => setChecked(new Set(categories.map((c) => c.id)));
  const handleNone = () => setChecked(new Set());

  const handleContinue = () => {
    if (checked.size === 0) return;
    onComplete(Array.from(checked));
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Audit Scope Selection</h1>
        <p className="page-desc">
          Select the OSHA programs that apply to this facility and audit. Only checked programs
          will appear in the audit. Use presets for common scenarios or customize manually.
        </p>
      </div>

      {/* Presets */}
      <div className="card">
        <h2 className="card-title">Quick Presets</h2>
        <div className="preset-grid">
          {PRESETS.map((p) => (
            <button key={p.label} className="btn btn-outline" onClick={() => applyPreset(p)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category Checklist */}
      <div className="card">
        <div className="card-title-row">
          <h2 className="card-title">
            Select Programs ({checked.size} of {categories.length} selected)
          </h2>
          <div className="btn-group-sm">
            <button className="btn-text" onClick={handleAll}>Select All</button>
            <span className="divider">|</span>
            <button className="btn-text" onClick={handleNone}>Clear All</button>
          </div>
        </div>

        <div className="category-grid">
          {categories.map((cat) => (
            <label key={cat.id} className={`category-card ${checked.has(cat.id) ? "selected" : ""}`}>
              <input
                type="checkbox"
                checked={checked.has(cat.id)}
                onChange={() => toggle(cat.id)}
                className="category-checkbox"
              />
              <div className="category-card-body">
                <div className="category-card-name">{cat.name}</div>
                <div className="category-card-citation">{cat.citation}</div>
                <div className="category-card-desc">{cat.description}</div>
                <div className="category-card-count">
                  {cat.criteria.length} audit items
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="btn-row">
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        <button
          className="btn btn-primary btn-lg"
          onClick={handleContinue}
          disabled={checked.size === 0}
        >
          Begin Audit ({checked.size} programs) →
        </button>
      </div>
    </div>
  );
}
